import api from "../utils/api.js";

import { EventBus } from "../../utilities/eventBus.js";
import { trackEvent, eventNames } from "../utils/tracking";

const auth_uri = api.authUri();

export const auth = {
    namespaced: true,
    state: {
        authToken: null,
        customerToken: null,
        user: {},
    },
    getters: {
        getAuthToken(state) {
            return state.authToken;
        },
        getUser(state) {
            return state.user;
        },
        getCustomerToken(state) {
            return state.customerToken;
        },
    },
    mutations: {
        authenticate(state, authenticated) {
            state.authenticated = authenticated;
        },
        updateUser(state, user) {
            state.user = Object.assign({}, state.user, user);
        },
        login(state, payload) {
            state.authToken = payload.authToken;
            state.user = payload.user;
        },
        logOut(state, payload) {
            state.authToken = null;
            state.user =
                state.user.rememberMe &&
                ((payload && !payload.force) || !payload)
                    ? {
                          ...state.user,
                          password: null,
                      }
                    : {};
        },
        updateCustomerToken(state, access_token) {
            state.customerToken = access_token;
        },
    },
    actions: {
        authenticate(context, payload) {
            return api
                .post(auth_uri, {
                    email: payload.email,
                    password: payload.password,
                })
                .then((response) => {
                    if (typeof window.FirebasePlugin !== "undefined") {
                        window.FirebasePlugin.setUserId(
                            response.body.user.user_id
                        );
                    }

                    context.commit("login", {
                        user: {
                            ...response.body.user,
                            email: payload.email,
                            password: payload.password,
                            rememberMe: payload.rememberMe,
                        },
                        authToken: response.body.token,
                    });

                    //Reset state post login
                    context.commit("savedProducts/resetState", null, {
                        root: true,
                    });
                    context.commit("savedOutfits/resetState", null, {
                        root: true,
                    });
                    context.commit("generatedOutfits/resetState", null, {
                        root: true,
                    });
                })
                .catch((error) => {
                    throw error.body.message;
                });
        },
        async registerUser(context, payload) {
            try {
                await context.dispatch("createUser", payload.user);
                await context.dispatch(
                    "saveUserOptionValues",
                    payload.userOptionValues
                );
            } catch (e) {
                throw e;
            }
        },
        createUser(context, user) {
            let uri = api.registerUri();

            return api
                .post(uri, user)
                .then((response) => {
                    trackEvent(eventNames.CREATE_ACCOUNT, {});
                    if (typeof window.FirebasePlugin !== "undefined") {
                        window.FirebasePlugin.setUserId(
                            response.body.user.user_id
                        );
                    }

                    context.commit("login", {
                        user: response.body.user,
                        authToken: response.body.token,
                    });
                })
                .catch((error) => {
                    throw error.body;
                });
        },
        updateUser(context, payload) {
            let uri = api.accountUri() + "update",
                data = {
                    profile_first_name: payload.user.profile_first_name,
                    profile_last_name: payload.user.profile_last_name,
                    user_email: payload.user.user_email,
                };

            if (
                payload.credentials.new_password &&
                payload.credentials.new_password_confirm
            ) {
                if (
                    payload.credentials.new_password !=
                    payload.credentials.new_password_confirm
                ) {
                    return Promise.reject(
                        "New password and confirmation do not match"
                    );
                } else {
                    data.user_password = payload.credentials.new_password;
                }
            }

            return api
                .post(uri, data)
                .then((response) => {
                    context.state.user = response.body.user;
                    context.commit("updateUser", context.state.user);
                    EventBus.$emit("notification", response.body.message);
                })
                .catch((error) => {
                    console.log("user update error:", error);
                    throw error.body.message;
                });
        },
        updateUserPhoto(context, payload) {
            let options = new FileUploadOptions(),
                uri = api.accountUri() + "updatephoto";

            options.fileKey = "photo";
            options.fileName = payload.imageUri.substr(
                payload.imageUri.lastIndexOf("/") + 1
            );
            options.mimeType = "image/jpeg";
            options.headers = {
                "X-Auth-Token": this.getters["auth/getAuthToken"],
                Accept: "application/json",
            };

            let fileTransfer = new FileTransfer();
            fileTransfer.upload(
                payload.imageUri,
                encodeURI(uri),
                (response) => {
                    let body = JSON.parse(response.response);
                    context.state.user = body.user;
                    context.commit("updateUser", context.state.user);
                    EventBus.$emit("notification", body.message);
                },
                (error) => {},
                options
            );
        },
        updateUserRiskValue(context, value) {
            let uri = api.accountUri() + "update-risk";

            return api
                .post(uri, { profile_risk: value })
                .then((response) => {
                    context.state.user.profile_risk = value;
                    context.commit("updateUser", context.state.user);
                    EventBus.$emit("notification", "Preference Updated!");
                })
                .catch((error) => {
                    throw error.body.message;
                });
        },
        getCustomerToken(context) {
            let uri = api.getCustomerTokenUri();

            return api
                .get(uri)
                .then((res) => {
                    context.commit("updateCustomerToken", res.access_token);
                })
                .catch((error) => {
                    throw error;
                });
        },
        saveUserOptionValues(context, userOptionValues) {
            let uri = api.preferencesUri();
            return api
                .post(uri, {
                    product_option_value_ids: userOptionValues,
                })
                .then((response) => {
                    return context.dispatch("quizzes/fetchQuizProfile", null, {
                        root: true,
                    });
                })
                .catch((response) => {
                    throw response.body.message;
                });
        },
        sendResetEmail(context, email) {
            let uri = api.sendResetEmailUri();

            return api
                .post(uri, { email: email })
                .then((response) => {
                    return response.body.message;
                })
                .catch((response) => {
                    throw response.body.message;
                });
        },
        resetPassword(context, user) {
            let uri = api.passwordResetUri();

            return api
                .post(uri, user)
                .then((response) => {
                    if (!user.code) {
                        EventBus.$emit(
                            "notification",
                            "Your Password has been Updated!"
                        );
                    }
                    return response.body.message;
                })
                .catch((response) => {
                    throw response.body.message;
                });
        },
        refreshUser(context) {
            const uri = api.accountUri();

            return api
                .get(uri)
                .then((response) => {
                    context.commit("updateUser", response.user);
                })
                .catch((response) => {
                    throw response;
                });
        },
        addDeviceToken(context, payload) {
            let uri = api.accountUri() + "adddevicetoken";

            return api
                .post(uri, { token: payload.token })
                .then((response) => {})
                .catch((error) => {});
        },
    },
};
